import { SplitIcon } from 'components/icons';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { InvoiceAmountDetailsTotalsDto, InvoiceData, InvoiceTotals } from 'types/invoices';
import { cn } from 'utils/cn';
import { TOTALS_LABEL_CELL, TOTALS_VALUE_CELL } from '../layout/views/totals-tab/InvoiceTotalsCard';
import { formatShareRate } from './paymentShare';

interface Props {
    invoice: InvoiceData;
    /** This copy's share: what the invoice is paid, tracked and exported with. */
    invoiceTotals?: InvoiceTotals;
}

function vatRows(totals: InvoiceAmountDetailsTotalsDto | undefined, keyPrefix: string) {
    return Object.entries(totals?.vatRateAmounts ?? {}).map(([vatRate, amount]) => (
        <tr key={`${keyPrefix}-${vatRate}`}>
            <td className={TOTALS_LABEL_CELL}>
                <FormattedMessage id="invoices.totals-tab.vat-rate-with-colon" values={{ rate: vatRate }} />
            </td>
            <td className={TOTALS_VALUE_CELL}>
                <MoneyDisplay amount={amount} currency={totals?.currencyTypeCode} />
            </td>
        </tr>
    ));
}

/**
 * The totals of a shared-payment copy: the full invoice the payers share, then this payer's share of it,
 * which is the amount the invoice is payable with.
 */
export default function SharedPaymentTotalsCard({ invoice, invoiceTotals }: Props) {
    const full = invoice.preSplitTotals?.paymentCurrency;
    const share = invoiceTotals?.paymentCurrency;
    const currency = share?.currencyTypeCode ?? full?.currencyTypeCode;
    const prepaymentAmount = share?.invoice.prepaymentAmount ?? 0;

    return (
        <div>
            <div className="flex flex-col items-end gap-3 p-6">
                <table>
                    <tbody>
                        <tr>
                            <td className={TOTALS_LABEL_CELL}>
                                <FormattedMessage id="invoices.totals-tab.total-amount-wo-vat" />
                            </td>
                            <td className={TOTALS_VALUE_CELL}>
                                <MoneyDisplay amount={full?.invoice.totalWithoutVat ?? 0} currency={currency} />
                            </td>
                        </tr>
                        {vatRows(full, 'full-vat')}
                        <tr>
                            <td className={TOTALS_LABEL_CELL}>
                                <FormattedMessage id="invoices.totals-tab.total-amount-with-vat" />
                            </td>
                            <td className={TOTALS_VALUE_CELL}>
                                <MoneyDisplay amount={full?.invoice.totalWithVat ?? 0} currency={currency} />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <hr className="border-divider w-full max-w-[360px] border-t" />
                <table>
                    <tbody>
                        <tr>
                            <td
                                colSpan={2}
                                className="text-fg flex items-center justify-end gap-1.5 p-0 pb-1 text-right text-[12px] leading-[1.375rem] font-semibold"
                            >
                                <SplitIcon className="size-3.5" />
                                <FormattedMessage
                                    id="invoices.payers.totals.share-of"
                                    values={{
                                        payer: invoice.clientParty.name,
                                        rate: formatShareRate(invoice.paymentShareRate),
                                    }}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className={TOTALS_LABEL_CELL}>
                                <FormattedMessage id="invoices.payers.totals.share-of-net" />
                            </td>
                            <td className={TOTALS_VALUE_CELL}>
                                <MoneyDisplay amount={share?.invoice.totalWithoutVat ?? 0} currency={currency} />
                            </td>
                        </tr>
                        {vatRows(share, 'share-vat').map((row) => (
                            <Fragment key={row.key}>{row}</Fragment>
                        ))}
                        {prepaymentAmount ? (
                            <tr>
                                <td className={TOTALS_LABEL_CELL}>
                                    <FormattedMessage id="invoices.totals-tab.prepayments" />
                                </td>
                                <td className={TOTALS_VALUE_CELL}>
                                    <MoneyDisplay amount={prepaymentAmount * -1} currency={currency} />
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
            <div className="bg-secondary/70 text-secondary-foreground flex justify-end rounded-b-[7px] px-6 py-[18px]">
                <table>
                    <tbody>
                        <tr>
                            <td className="p-0 text-[12px] leading-[1.375rem] font-semibold">
                                <FormattedMessage id="invoices.payers.totals.total-payable" />
                            </td>
                            <td className={cn(TOTALS_VALUE_CELL, 'text-secondary-foreground text-base font-semibold')}>
                                <MoneyDisplay
                                    amount={(share?.invoice.totalWithVat ?? 0) - prepaymentAmount}
                                    currency={currency}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p className="text-caption text-fg-secondary px-6 py-3">
                <FormattedMessage id="invoices.payers.totals.payable-footnote" />
            </p>
        </div>
    );
}
